import { useState } from 'react';

import { getBy, useStore } from '@tanstack/react-form';

import {
  ArrayTextField as UadArrayTextField,
  type ArrayTextFieldProps as UadArrayTextFieldProps,
} from '@iconik-io/uad';

import { useFieldContext } from '../app_form_context';
import { getFieldErrorMessage } from '../get_field_error_message';

export type ArrayTextFieldProps = Omit<
  UadArrayTextFieldProps,
  | 'errorMessage'
  | 'isInvalid'
  | 'itemErrorMessages'
  | 'name'
  | 'onBlur'
  | 'onChange'
  | 'value'
>;

/**
 * Array text field bound to the surrounding form.
 *
 * Valibot issues raised against the array itself, such as `v.minLength`, are
 * shown as the field error. Issues raised against an element, such as
 * `v.array(v.pipe(v.string(), v.nonEmpty()))`, are shown against that row.
 *
 * Validation runs on submit, so row errors appear after a submission is
 * attempted and are hidden again as soon as the values change.
 */
export function ArrayTextField(props: ArrayTextFieldProps) {
  const field = useFieldContext<string[]>();
  const [submissionAttemptsAtLastEdit, setSubmissionAttemptsAtLastEdit] =
    useState(-1);

  const submissionAttempts = useStore(
    field.form.store,
    (state) => state.submissionAttempts,
  );

  const itemErrorMessages = useStore(field.form.store, (state) => {
    const values: unknown = getBy(state.values, field.name);

    return (Array.isArray(values) ? values : []).map((_, index) =>
      getFieldErrorMessage(
        state.fieldMeta[`${field.name}[${index}]`]?.errors ?? [],
      ),
    );
  });

  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

  // Row errors are keyed by index and only refresh on submit, so after a failed
  // submit an add or remove would leave them against the wrong value. Hide them
  // on any edit; the next submit produces a set that matches again.
  const showItemErrors =
    submissionAttempts > 0 &&
    submissionAttemptsAtLastEdit !== submissionAttempts;

  return (
    <UadArrayTextField
      name={field.name}
      value={field.state.value}
      onBlur={field.handleBlur}
      onChange={(value) => {
        setSubmissionAttemptsAtLastEdit(submissionAttempts);
        field.handleChange(value);
      }}
      isInvalid={isInvalid}
      errorMessage={
        isInvalid ? getFieldErrorMessage(field.state.meta.errors) : undefined
      }
      itemErrorMessages={showItemErrors ? itemErrorMessages : undefined}
      {...props}
    />
  );
}
